import { useState } from 'react';
import { ThumbsUp, ThumbsDown, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSubmitAiFeedbackMutation, useUpdateAiFeedbackMutation } from '@/services/ApiService';
import { FeedbackType } from '@/apiTypes/AiTypes';
import { cn } from '@/lib/utils';

interface MessageFeedbackProps {
    messageId: string;
    submittedFeedback?: { type: FeedbackType; id: string };
    onFeedbackSubmitted: (feedbackType: FeedbackType, feedbackId: string) => void;
}

const feedbackConfig: Record<FeedbackType, { icon: typeof ThumbsUp; label: string }> = {
    positive: { icon: ThumbsUp, label: 'Helpful' },
    negative: { icon: ThumbsDown, label: 'Not helpful' },
    flag: { icon: Flag, label: 'Report issue' },
};

const feedbackTypes: FeedbackType[] = ['positive', 'negative', 'flag'];

export function MessageFeedback({ messageId, submittedFeedback, onFeedbackSubmitted }: MessageFeedbackProps) {
    const [openPopover, setOpenPopover] = useState<FeedbackType | null>(null);
    const [reason, setReason] = useState('');
    const [submitFeedback, { isLoading: isSubmitting }] = useSubmitAiFeedbackMutation();
    const [updateFeedback, { isLoading: isUpdating }] = useUpdateAiFeedbackMutation();

    const isLoading = isSubmitting || isUpdating;

    const handleSubmit = async (feedbackType: FeedbackType) => {
        let result;
        if (submittedFeedback?.id) {
            result = await updateFeedback({
                feedbackId: submittedFeedback.id,
                feedbackType,
                reason: reason.trim() || undefined,
            });
        }
        else {
            result = await submitFeedback({
                messageId,
                feedbackType,
                reason: reason.trim() || undefined,
            });
        }
        if (result.data) {
            onFeedbackSubmitted(feedbackType, result.data.id);
            setOpenPopover(null);
            setReason('');
        }
    };

    const handleCancel = () => {
        setOpenPopover(null);
        setReason('');
    };

    return (
        <div className="flex gap-1 mt-1">
            {feedbackTypes.map((type) => {
                const { icon: Icon, label } = feedbackConfig[type];
                const isSelected = submittedFeedback?.type === type;
                const isOtherSelected = submittedFeedback && submittedFeedback.type !== type;

                return (
                    <Popover
                        key={type}
                        open={openPopover === type}
                        onOpenChange={open => setOpenPopover(open ? type : null)}
                    >
                        <PopoverTrigger asChild>
                            <button
                                className={cn(
                                    'p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground',
                                    isSelected && 'text-primary dark:text-accent',
                                    isOtherSelected && 'opacity-40',
                                )}
                                aria-label={label}
                                data-track-click="AI Chat Feedback Clicked"
                                data-track-location="ai-chat"
                                data-track-button-name={`ai-chat-feedback-${type}`}
                            >
                                <Icon className="size-4" strokeWidth={2.5} />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72" align="start">
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <Icon className="size-4" />
                                    {label}
                                </div>
                                <Textarea
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    placeholder="Add optional feedback..."
                                    className="min-h-20 resize-none text-sm"
                                />
                                <div className="flex gap-2 justify-end">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleCancel}
                                        disabled={isLoading}
                                        data-track-click="AI Chat Feedback Cancel Clicked"
                                        data-track-location="ai-chat"
                                        data-track-button-name="ai-chat-feedback-cancel"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => handleSubmit(type)}
                                        disabled={isLoading}
                                        data-track-click="AI Chat Feedback Submit Clicked"
                                        data-track-location="ai-chat"
                                        data-track-button-name="ai-chat-feedback-submit"
                                    >
                                        {isLoading ? 'Submitting...' : 'Submit'}
                                    </Button>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                );
            })}
        </div>
    );
}
